import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";
import type { PropertyPublic } from "@townsy/contracts";
import { useTownSession } from "../app/TownSessionContext";
import { districtOfStreet } from "../lib/directoryFilters";
import { useTownStore } from "../state/store";
import { Dialog } from "./Dialog";
import { DirectoryList } from "./DirectoryList";
import { OUT_OF_CAR_FIRST } from "../net/vehicles";

/**
 * In-town directory/search overlay (same actions as the world; the /directory route is the full page). World v5
 * (ADR-0041): it opens on the district the visitor is standing in (All is one click away), or on the district of the
 * street name clicked on the map. One continuous town (ADR-0042 decision 5): Go there frames any place in Overview.
 */
export function DirectoryPanel() {
  const navigate = useNavigate();
  const session = useTownSession();
  const open = useTownStore((s) => s.ui.directoryOpen);
  const street = useTownStore((s) => s.ui.directoryStreet);
  const districtId = useTownStore((s) => s.districtId);
  const toggle = useTownStore((s) => s.toggleDirectory);
  const items = useTownStore(useShallow((s) => s.propertyIds.map((id) => s.properties[id]).filter((p): p is PropertyPublic => Boolean(p))));
  const { pending, roomKind, driving, sceneReady } = useTownStore(
    useShallow((s) => ({
      pending: s.ui.transitionPending,
      roomKind: s.room.kind,
      driving: s.ui.drivingVehicleId !== null,
      sceneReady: s.ui.sceneStatus === "ready",
    })),
  );
  if (!open) return null;
  return (
    <Dialog title="Directory" wide onClose={() => toggle(false)}>
      <DirectoryList
        // The district filter starts on the district the visitor is in when the directory opens; walking or driving across
        // a district line while it is open (ADR-0042: the store follows the position) never resets the visitor's filters.
        key={street ?? ""}
        items={items}
        initialStreet={street}
        initialDistrict={(street ? districtOfStreet(street, items) : null) ?? districtId}
        currentDistrictId={districtId}
        onOpen={(id) => {
          toggle(false);
          navigate(`/place/${id}`);
        }}
        onEnter={(id) => {
          toggle(false);
          // Another district's door is far away (one town street, ADR-0042 decision 5): enter it by the explicit fast
          // travel (the server authorizes it); in the visitor's own district Enter works as before.
          const elsewhere = items.find((p) => p.id === id)?.districtId !== districtId;
          void (elsewhere ? session?.fastTravel(id) : session?.enterProperty(id));
        }}
        onGoToRealEstate={
          session?.realEstateOfficeId
            ? () => {
                toggle(false);
                void session.fastTravel(session.realEstateOfficeId!);
              }
            : undefined
        }
        // Far places in the one town: frame them from above (leaving the current building first; ADR-0042 decision 5).
        onGoThere={
          session
            ? (id) => {
                toggle(false);
                session.goToProperty(id);
              }
            : undefined
        }
        goThereDisabledReason={pending ? "Moving…" : !sceneReady ? "The 3D view is still loading" : null}
        enterDisabledReason={pending ? "Moving…" : roomKind === "interior" ? "Exit the current building first" : driving ? OUT_OF_CAR_FIRST : null}
        goDisabledReason={pending ? "Moving…" : driving ? OUT_OF_CAR_FIRST : null}
        onCheckoutOpened={() => toggle(false)}
      />
    </Dialog>
  );
}
